import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Wallet, Plus, Loader2, Upload, Download, FileText, ShieldCheck, FileCheck2, Users } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { StatCard } from "@/components/common/StatCard";
import { TablaAmortizacionView } from "@/components/creditos/TablaAmortizacionView";
import { esCreditoFactoraje, estatusCredito, plazoCredito } from "@/lib/factoraje";
import { PagadorFactorajeSelect } from "@/components/creditos/PagadorFactorajeSelect";
import { TablaFactoraje } from "@/components/creditos/TablaFactoraje";
import { TablaAmortizacionCredito } from "@/components/creditos/TablaAmortizacionCredito";
import { TablaRentasCredito, LeyendaRentasCubiertas } from "@/components/creditos/TablaArrendamiento";
import { esCreditoArrendamiento, MSG_PAGO_RENTAS_CUBIERTAS, pagoPorOpcionTerminal, TITULO_RENTAS } from "@/lib/arrendamiento";
import { PrepagoCapital } from "@/components/creditos/PrepagoCapital";
import { OpcionTerminalBloque } from "@/components/creditos/opcionTerminal/OpcionTerminalBloque";
import { BienesDevueltos, useBienesDevueltos } from "@/components/creditos/opcionTerminal/BienesDevueltos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, apiError, listResource } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { sugeridoDePago, ivaEtiquetaGc } from "@/lib/sugeridoPago";
import { hoyMX, useReversionFechaAnterior, creditoSinRevertibles } from "@/lib/fechaPago";
import { MoneyInput } from "@/components/common/MoneyInput";
import { DiferidosPanel } from "@/components/common/DiferidosPanel";
import { useAuth } from "@/context/AuthContext";
import { BandejaPendientes } from "@/components/cliente/BandejaPendientes";
import { CancelarPagoButton } from "@/components/common/CancelarPagoButton";
import { CancelarCreditoButton } from "@/components/creditos/CancelarCreditoButton";
import { CorregirFechasButton } from "@/components/creditos/CorregirFechasButton";
import { ModificarTasaButton } from "@/components/creditos/ModificarTasaButton";
import { ReciboPagoButton } from "@/components/common/ReciboPagoButton";
import { ExportExcelButton } from "@/components/common/ExportExcelButton";
import { TooltipButton } from "@/components/common/Tooltips";
import { ExigibilidadGrupoBloque } from "@/components/ExigibilidadGrupoBloque";
import {
  HojasIntegrantesButton, HojaIntegranteButton,
} from "@/components/HojasIntegrantesButtons";
import { AcuseDesembolsoIntegranteButton } from "@/components/AcuseDesembolsoIntegranteButton";
import { AvisosDatosFiscales, EtiquetaSinDatosFiscales } from "@/components/fiscal/AvisosDatosFiscales";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = {
  contrato_firmado: "Contrato firmado", identificacion: "Identificación oficial",
  comprobante_domicilio: "Comprobante de domicilio", comprobante_ingresos: "Comprobante de ingresos",
  estados_cuenta_bancarios: "Estados de cuenta bancarios",
  recibos_nomina: "Recibos de nómina", carta_convenio_descuento: "Carta convenio de descuento",
  factura_o_carta_factura: "Factura / carta factura", poliza_seguro_auto: "Póliza de seguro del vehículo",
  escritura_titulo_propiedad: "Escritura / título de propiedad", certificado_libertad_gravamen: "Certificado de libertad de gravamen",
  boleta_predial_al_corriente: "Boleta predial al corriente", avaluo: "Avalúo",
  poliza_seguro_inmueble: "Póliza de seguro del inmueble", comprobante_propiedad: "Comprobante de propiedad",
  cotizacion_o_proforma: "Cotización o proforma", comprobante_actividad: "Comprobante de actividad",
  documento_garantia: "Documento de la garantía", plan_inversion_doc: "Plan de inversión",
  cotizacion_proveedor: "Cotización del proveedor", facturas_derechos_credito: "Facturas / derechos de crédito",
};
const docLabel = (t) => DOC_LABELS[t] || String(t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Cuota de crédito (capital + interés, SIN IVA): del resumen sellado o derivada del 1er renglón regular.
const cuotaFijaDe = (cred) => {
  if (cred?.resumen_amortizacion?.cuota_credito_fija != null) return cred.resumen_amortizacion.cuota_credito_fija;
  const f = (cred?.tabla_amortizacion || []).find((x) => !x.es_proporcional);
  return f ? Math.round((Number(f.capital || 0) + Number(f.interes || 0)) * 100) / 100 : null;
};

// Pago TOTAL fijo (capital + interés + IVA) — modalidad francesa_total_fijo (jul-2026).
const totalFijoDe = (cred) => {
  if (cred?.resumen_amortizacion?.pago_total_fijo != null) return cred.resumen_amortizacion.pago_total_fijo;
  const f = (cred?.tabla_amortizacion || []).find((x) => !x.es_proporcional);
  return f ? Number(f.pago || 0) : null;
};

const BadgeMigrada = ({ c, testid }) => (
  <span data-testid={testid}
    title={`Cartera migrada de otro sistema${c.numero_credito_anterior ? ` · crédito anterior ${c.numero_credito_anterior}` : ""}`}
    className="inline-flex w-fit items-center rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
    Cartera migrada{c.numero_credito_anterior ? ` · ${c.numero_credito_anterior}` : ""}
  </span>
);

const COLS = [
  { key: "numero_credito", label: "No. crédito", render: (r) => (
      <span className="inline-flex flex-col gap-0.5">
        <span>{r.numero_credito}</span>
        {r.cartera_migrada && <BadgeMigrada c={r} testid={`cartera-badge-migrada-${r.id}`} />}
        {r.sin_datos_fiscales && <EtiquetaSinDatosFiscales testid={`cartera-badge-sin-datos-fiscales-${r.id}`} />}
      </span>
    ) },
  { key: "cliente", label: "Cliente" },
  { key: "asesora", label: "Asesora" },
  { key: "producto", label: "Producto" },
  // Fase 5 (feb-2026) — Badge de línea/disposición para créditos revolventes. Un crédito
  // simple (linea_id=null) muestra "—" (sin efecto visual). El <Link> tiene stopPropagation
  // para que el click en el badge navegue al detalle de la línea SIN abrir el modal del
  // crédito hijo. Formato: "LC-2026-XXXX · D01" (número + disposición, ambos identifican al
  // vuelo cuál disposición es de qué línea sin abrir nada).
  { key: "linea", label: "Línea", render: (r) => (
      r.linea_id ? (
        <Link
          to={`/lineas/${r.linea_id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
          data-testid={`cartera-badge-linea-${r.id}`}
          title={`Abrir línea ${r.linea_numero}`}
        >
          {r.linea_numero}
          {r.disposicion_numero != null && (
            <span className="text-emerald-500">· D{String(r.disposicion_numero).padStart(2, "0")}</span>
          )}
        </Link>
      ) : <span className="text-slate-300">—</span>
    ) },
  { key: "monto_original", label: "Monto original", kind: "currency" },
  { key: "saldo_insoluto", label: "Saldo insoluto", kind: "currency" },
  { key: "dias_atraso", label: "Días atraso" },
  { key: "estatus", label: "Estatus", render: (r) => <StatusBadge value={estatusCredito(r)} /> },
];

export default function Cartera() {
  const { can, hasArea, user } = useAuth();
  const puedePago = can("pagos", "crear");
  const [creditos, setCreditos] = useState([]);
  // Fase 5 (feb-2026) — Filtro local por tipo (aditivo, sin round-trip al backend).
  // 'todos' = incluye ambos; 'creditos' = simples/automotriz/hipotecario/personal (linea_id ausente);
  // 'disposiciones' = solo créditos hijos de línea (linea_id presente).
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [selected, setSelected] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pago, setPago] = useState({});
  const [docsCred, setDocsCred] = useState([]);
  const [upBusy, setUpBusy] = useState(false);
  const fileRef = useRef(null);
  const bienesDev = useBienesDevueltos();
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const data = await listResource("creditos");
    // oct-2026 · Asesora asignada: vive en el cliente (cobrador_nombre); el crédito la hereda por cliente_id.
    let asesoras = {};
    try {
      const cl = await listResource("clientes");
      asesoras = Object.fromEntries((cl || []).map((c) => [c.id, c.cobrador_nombre || ""]));
    } catch { /* si falla, la columna muestra "—" y la cartera carga igual */ }
    setCreditos((data || []).map((c) => ({
      ...c,
      asesora: c.cliente_id ? (asesoras[c.cliente_id] || "Sin asignar") : "—",
    })));
  };
  useEffect(() => { load(); }, []);
  // Liga desde Perfil 360: /sofom/cartera?credito=<id o número> abre el detalle.
  useEffect(() => {
    const q = params.get("credito");
    const row = q && creditos.find((c) => c.id === q || c.numero_credito === q);
    if (!row) return;
    openDetail(row);
    params.delete("credito");
    setParams(params, { replace: true });
  }, [creditos]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (row) => {
    setSelected(row);
    const data = await api.get("/pagos", { params: { credito_id: row.id } }).then((r) => r.data);
    setPagos(data);
    api.get("/documentos", { params: { credito_id: row.id } }).then((r) => setDocsCred(r.data)).catch(() => setDocsCred([]));
    // Fase 3: deriva mora/vencimiento al día si el crédito tiene tabla.
    // sep-2026: un crédito cancelado NO se recalcula (el backend también lo bloquea).
    if (row.tabla_amortizacion?.length && !esCancelado(row)) {
      try {
        const upd = await api.post(`/creditos/${row.id}/recalcular-mora`).then((r) => r.data);
        setSelected(upd);
      } catch (e) { console.debug("recalcular-mora omitido (legacy o sin permiso):", e?.response?.status); }
    }
  };

  const cargarDocs = async (cid) => {
    const r = await api.get("/documentos", { params: { credito_id: cid } });
    setDocsCred(r.data);
  };

  const descargarDoc = async (docId) => {
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch { toast.error("No se pudo generar el enlace de descarga"); }
  };

  const subirContrato = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const esPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!esPdf) { toast.error("El contrato firmado debe ser un archivo PDF"); return; }
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "contrato_firmado");
      fd.append("categoria", "credito");
      fd.append("clasificacion", "Contrato");
      fd.append("credito_id", selected.id);
      fd.append("cliente_id", selected.cliente_id || "");
      await api.post("/documentos/upload", fd);
      toast.success("Contrato firmado resguardado (cifrado en reposo)");
      await cargarDocs(selected.id);
    } catch { toast.error("No se pudo subir el contrato"); }
    finally { setUpBusy(false); }
  };

  const proximaCuota = (cred) =>
    (cred?.tabla_amortizacion || []).find((c) => c.estatus !== "Pagada");

  // Diferimiento (jun-2026): mora congelada + gastos de cobranza diferidos pendientes
  const difMora = (c) => Number(c?.total_moratorios_diferidos_pendientes || 0);
  const difGc = (c) => Number(c?.total_gastos_cobranza_diferidos_pendientes || 0);
  const round2 = (n) => Math.round(n * 100) / 100;
  const pagoPorOt = pagoPorOpcionTerminal(selected);

  const openPay = (integ = null) => {
    const cuota = integ
      ? (integ.tabla_amortizacion || []).find((c) => c.estatus !== "Pagada")
      : proximaCuota(selected);
    // Sugerido UNIFICADO (jul-2026): util compartida con Pagos/Cobranza. Lee cuota pendiente
    // + mora corriente + diferidos de valores persistidos por el motor (base 360).
    // Integrante grupal: solo su cuota (los diferidos viven a nivel crédito consolidado).
    const sug = sugeridoDePago(integ ? {} : selected, cuota);
    setPago({
      credito_id: selected.id, numero_credito: selected.numero_credito, cliente: selected.cliente,
      cliente_id: selected.cliente_id, fecha_pago: hoyMX(),
      tipo_pago: "normal",
      monto: sug.total > 0 && (integ || !pagoPorOpcionTerminal(selected)) ? String(sug.total) : "",
      medio_pago: "Transferencia SPEI", banco: "", referencia: "", pago_efectivo: false, pago_tercero: false,
      nombre_tercero: "",
      integrante_cliente_id: integ ? integ.cliente_id : "",
      integrante_nombre: integ ? integ.cliente : "",
      ...(esCreditoFactoraje(selected) ? { pagador: "deudor" } : {}),
    });
    setPayOpen(true);
  };

  const setP = (k, v) => setPago((p) => ({ ...p, [k]: v }));

  // Build G: pago con fecha anterior → cargos por pago tardío que se revertirán (aviso y sugerido sin ellos).
  const revPago = useReversionFechaAnterior(selected?.id, pago.fecha_pago, {
    integranteId: pago.integrante_cliente_id, activo: payOpen && pago.tipo_pago !== "abono_capital",
  });
  const credPago = creditoSinRevertibles(selected, revPago);
  useEffect(() => {
    const pc = proximaCuota(selected);
    if (!payOpen || !pc || !revPago || pago.integrante_cliente_id || pagoPorOt) return;
    const base = String(sugeridoDePago(selected, pc).total);
    const ajus = String(sugeridoDePago(credPago, pc).total);
    setPago((p) => ([base, ajus].includes(String(p.monto)) ? { ...p, monto: ajus } : p));
  }, [revPago]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadDetail = async () => {
    if (!selected) return;
    await load();
    const updated = await api.get(`/creditos/${selected.id}`).then((r) => r.data);
    setSelected(updated);
    const data = await api.get("/pagos", { params: { credito_id: selected.id } }).then((r) => r.data);
    setPagos(data);
  };

  const savePago = async () => {
    if (!pago.monto || Number(pago.monto) <= 0) { toast.error("Captura el monto del pago"); return; }
    setSaving(true);
    const { integrante_nombre, ...rest } = pago;
    const body = { ...rest, monto: Number(pago.monto) };
    try {
      const { data: pg } = await api.post("/pagos", body);
      toast.success(pago.integrante_cliente_id
        ? `Pago aplicado a ${integrante_nombre} (su tabla del grupo).`
        : "Pago registrado. Aplicado contra la tabla de amortización.");
      if (Number(pg?.remanente_generado) > 0) toast.info(`Remanente a favor del cliente (por devolver): ${mxn(pg.remanente_generado)}`);
      setPayOpen(false);
      await load();
      const updated = await api.get(`/creditos/${selected.id}`).then((r) => r.data);
      setSelected(updated);
      const data = await api.get("/pagos", { params: { credito_id: selected.id } }).then((r) => r.data);
      setPagos(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail ? apiError(e) : "No se pudo registrar el pago");
    } finally {
      setSaving(false);
    }
  };

  // sep-2026 · Un crédito CANCELADO (cancelación lógica, 51ª LFPIORPI) sale de la cartera
  // operativa: no cuenta en los indicadores ni aparece en la tabla, pero se conserva y se
  // consulta con el filtro "Cancelados".
  const esCancelado = (c) => !!c.cancelado || c.estatus === "Cancelado";
  const activos = creditos.filter((c) => !esCancelado(c));
  const cancelados = creditos.filter(esCancelado);
  const sinDatosFiscales = creditos.filter((c) => c.sin_datos_fiscales && !esCancelado(c));
  const avisosFiscalesSel = selected
    && (selected.avisos_fiscales ?? creditos.find((c) => c.id === selected.id)?.avisos_fiscales);
  const total = activos.reduce((a, c) => a + Number(c.saldo_insoluto || 0), 0);
  const vigente = activos.filter((c) => c.estatus === "Vigente").reduce((a, c) => a + Number(c.saldo_insoluto || 0), 0);
  const vencida = activos.filter((c) => c.estatus === "Vencido").reduce((a, c) => a + Number(c.saldo_insoluto || 0), 0);

  return (
    <div>
      <PageHeader title="Cartera" subtitle="Créditos vigentes, vencidos y reestructurados — registro de pagos en línea">
        {hasArea && hasArea("cartera") && (
          <ExportExcelButton endpoint="/exportar/gestion/creditos" filename="cartera_creditos.xlsx" testid="export-creditos-btn" />
        )}
      </PageHeader>
      <div className="space-y-6 p-6 md:p-8">
        {user?.role === "admin_entidad" && <BandejaPendientes />}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <StatCard label="Créditos" value={activos.length} icon="Wallet" accent="primary" />
          <StatCard label="Cartera total" value={mxn(total)} icon="Landmark" accent="blue" />
          <StatCard label="Cartera vigente" value={mxn(vigente)} icon="TrendingUp" accent="green" />
          <StatCard label="Cartera vencida" value={mxn(vencida)} icon="TrendingDown" accent="red" />
        </div>

        {filtroTipo === "bienes_devueltos" ? (
          <BienesDevueltos bienes={bienesDev.bienes} cuenta={bienesDev.cuenta_bienes}
            onAbrir={(cid) => { const row = creditos.find((c) => c.id === cid); if (row) openDetail(row); }} />
        ) : <DataTable testid="table-cartera" columns={COLS} rows={creditos.filter((c) => {
          // Fase 5 (feb-2026) — Filtrado local por tipo. Aditivo: cuando `filtroTipo === 'todos'`
          // el comportamiento es idéntico al anterior a Fase 5.
          if (filtroTipo === "cancelados") return esCancelado(c);
          if (esCancelado(c)) return false;
          if (filtroTipo === "creditos") return !c.linea_id;
          if (filtroTipo === "disposiciones") return !!c.linea_id;
          if (filtroTipo === "sin_datos_fiscales") return !!c.sin_datos_fiscales;
          return true;
        })} onRowClick={openDetail}
          emptyLabel="Sin créditos en cartera" />}
        <div className="mt-2 flex items-center justify-between gap-4">
          <p className="text-xs text-slate-400">Haz clic en un crédito para ver el detalle y registrar pagos.</p>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Filtrar por tipo</Label>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="cartera-filtro-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos" data-testid="cartera-filtro-todos">Todos (en cartera)</SelectItem>
                <SelectItem value="creditos" data-testid="cartera-filtro-creditos">Créditos</SelectItem>
                <SelectItem value="disposiciones" data-testid="cartera-filtro-disposiciones">Disposiciones de línea</SelectItem>
                <SelectItem value="sin_datos_fiscales" data-testid="cartera-filtro-sin-datos-fiscales">
                  Sin datos fiscales ({sinDatosFiscales.length})
                </SelectItem>
                <SelectItem value="cancelados" data-testid="cartera-filtro-cancelados">
                  Cancelados ({cancelados.length})
                </SelectItem>
                {bienesDev.aplica && (
                  <SelectItem value="bienes_devueltos" data-testid="cartera-filtro-bienes-devueltos">
                    Bienes devueltos ({bienesDev.bienes.length})
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Detalle de crédito */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <Wallet className="h-5 w-5 text-primary" /> {selected.numero_credito} — {selected.cliente}
                  {selected.cartera_migrada && <BadgeMigrada c={selected} testid="cartera-detalle-badge-migrada" />}
                  {avisosFiscalesSel?.length > 0 && <EtiquetaSinDatosFiscales testid="cartera-detalle-badge-sin-datos-fiscales" />}
                </DialogTitle>
                <DialogDescription className="sr-only">Detalle del crédito, tabla de amortización y pagos aplicados</DialogDescription>
              </DialogHeader>
              <AvisosDatosFiscales avisos={avisosFiscalesSel} testid="cartera-detalle-avisos-fiscales" />
              {/* sep-2026 · Cancelación lógica del crédito: solo el administrador de la entidad
                  ve el botón (el backend repite el candado y exige motivo). */}
              <div className="-mt-1 flex justify-end gap-2" data-testid="cartera-acciones-credito">
                <CorregirFechasButton
                  credito={selected}
                  onDone={async () => { setSelected(null); await load(); }}
                />
                <ModificarTasaButton
                  credito={selected}
                  pagos={pagos}
                  onDone={async () => { setSelected(null); await load(); }}
                />
                <CancelarCreditoButton
                  credito={selected}
                  onDone={async () => { setSelected(null); await load(); }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Saldo insoluto", mxn(selected.saldo_insoluto)],
                  ["Monto original", mxn(selected.monto_original)],
                  ["Capital vigente", mxn(selected.capital_vigente)],
                  ["Interés moratorio", mxn(selected.interes_moratorio)],
                  ...(Number(selected.iva_interes_moratorio) > 0
                    ? [["IVA de moratorios", mxn(selected.iva_interes_moratorio)]] : []),
                  ...(Number(selected.total_gastos_cobranza_pendientes) > 0
                    ? [["GC pendientes (+ IVA)", mxn(selected.total_gastos_cobranza_pendientes)]] : []),
                  ...(difMora(selected) + difGc(selected) > 0
                    ? [["Diferidos por cobrar", mxn(round2(difMora(selected) + difGc(selected)))]] : []),
                  ["Días de atraso", selected.dias_atraso],
                  ["Pagos realizados", selected.pagos_realizados],
                  ["Próximo pago", fdate(proximaCuota(selected)?.fecha_pago)],
                  ...(selected.tasa_ordinaria != null ? [["Tasa ordinaria", `${selected.tasa_ordinaria}%`]] : []),
                  ...(selected.total_pagar != null ? [["Total a pagar", mxn(selected.total_pagar)]] : []),
                  ...(selected.modalidad ? [["Modalidad", selected.modalidad]] : []),
                  ...(selected.cat != null ? [["CAT (anual)", `${selected.cat}%`]] : []),
                  ...(selected.remanente ? [["Remanente pendiente", mxn(selected.remanente.pendiente)]] : []),
                ].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={`credito-campo-${l}`}>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800 tabular-nums">{v}</p>
                  </div>
                ))}
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Estatus</p>
                  <div className="mt-1"><StatusBadge value={estatusCredito(selected)} /></div>
                  {esCreditoArrendamiento(selected) && (
                    <div className="mt-1"><LeyendaRentasCubiertas tabla={selected.tabla_amortizacion} dataTestid="credito-leyenda-rentas-cubiertas" /></div>
                  )}
                </div>
              </div>
              {selected.cat != null && selected.cat_meta && (
                <p className="mt-2 text-[11px] text-slate-400" data-testid="credito-cat-nota">
                  {selected.cat_meta.nota}. {selected.cat_meta.metodologia}.
                </p>
              )}

              {esCreditoArrendamiento(selected) && (
                <OpcionTerminalBloque credito={selected} onCambio={async () => { await reloadDetail(); await bienesDev.recargar(); }} />
              )}

              {/* Carátula de condiciones (Ley de Transparencia) */}
              <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="caratula-condiciones">
                <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-primary">
                  <FileText className="h-4 w-4" /> Carátula de condiciones
                </h4>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                  {[
                    ["Monto del crédito", mxn(selected.monto_original)],
                    ["Tasa ordinaria anual", `${selected.tasa_ordinaria ?? 0}%`],
                    ["Tasa moratoria anual", `${selected.tasa_moratoria ?? 0}%`],
                    ["CAT", selected.cat != null ? `${selected.cat}%` : "—"],
                    ["Comisión de apertura", `${mxn(selected.comision)}${Number(selected.comision) > 0 && Number(selected.monto_original) > 0 ? ` (${((selected.comision / selected.monto_original) * 100).toFixed(2)}%)` : ""}`],
                    ["IVA de la comisión", mxn(selected.iva_comision ?? selected.resumen_amortizacion?.iva_comision)],
                    ["Gastos de cobranza", selected.gastos_cobranza
                      ? `${mxn(selected.gastos_cobranza)}${selected.gc_etiqueta && selected.gc_etiqueta !== "Gastos de cobranza" ? ` · "${selected.gc_etiqueta}"` : ""}`
                      : "No aplica"],
                    ["Plazo", esCreditoFactoraje(selected) ? plazoCredito(selected)
                      : `${selected.plazo ?? "—"} (${selected.periodicidad ?? "—"})`],
                    ...(selected.tipo_amortizacion === "francesa_total_fijo"
                      ? [["Pago total fijo (capital + interés + IVA)",
                          totalFijoDe(selected) != null ? mxn(totalFijoDe(selected)) : "—"]]
                      : [[selected.tipo_amortizacion === "alemana" ? "Cuota inicial (capital + interés)" : "Cuota fija (capital + interés)",
                          cuotaFijaDe(selected) != null ? mxn(cuotaFijaDe(selected)) : "—"]]),
                    ["Pago 1er periodo (cuota + IVA)", mxn(selected.resumen_amortizacion?.pago_estimado)],
                    ["Total a pagar", mxn(selected.total_pagar ?? selected.resumen_amortizacion?.total_pagar)],
                    ["Fecha de disposición", fdate(selected.fecha_disposicion)],
                    // oct-2026 · El dinero pudo salir antes de que el crédito se capturara en
                    // el sistema: la carátula dice las dos fechas, sin contradecirse.
                    ...(selected.fecha_real_otorgamiento
                      ? [["Fecha real de otorgamiento", fdate(selected.fecha_real_otorgamiento)],
                         ["Contratación en el sistema",
                          fdate(selected.fecha_contratacion_sistema || selected.created_at)]]
                      : []),
                    ["Fecha de vencimiento", fdate(selected.fecha_vencimiento)],
                    ["Modalidad", selected.modalidad || "—"],
                  ].map(([l, v]) => (
                    <div key={l} className="rounded-md border border-slate-200 bg-white p-2" data-testid={`caratula-${l}`}>
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                      <p className="mt-0.5 text-sm font-semibold text-slate-800 tabular-nums">{v}</p>
                    </div>
                  ))}
                </div>
                {selected.cat != null && (
                  <p className="mt-2 text-[11px] font-medium text-amber-700">CAT informativo — sujeto a validación.</p>
                )}
                {selected.fecha_real_otorgamiento && (
                  <p className="mt-2 text-[11px] font-medium text-sky-800" data-testid="caratula-otorgamiento-real">
                    El dinero se entregó el {fdate(selected.fecha_real_otorgamiento)}, antes de que el
                    crédito se capturara en el sistema. Todos los efectos (tabla, intereses, IVA,
                    pólizas, información fiscal, CONDUSEF, cartera SIC y reportes PLD) quedaron en el
                    periodo {String(selected.fecha_real_otorgamiento).slice(0, 7)}.
                  </p>
                )}
                <p className="mt-1 text-[11px] text-slate-400">
                  Resumen interno de condiciones para resguardo y consulta. No sustituye la carátula formal del contrato emitido por la entidad.
                </p>
              </div>

              {/* Diferidos (jun-2026): mora congelada + GC diferidos, con pago adelantado */}
              {(difMora(selected) > 0 || difGc(selected) > 0 || (selected.moratorios_diferidos || []).length > 0) && (
                <div className="mt-3">
                  <DiferidosPanel
                    creditoId={selected.id}
                    moratoriosDiferidos={selected.moratorios_diferidos || []}
                    cargosDiferidos={(selected.cargos_cobranza || []).filter((c) => c.diferido && !c.revertido)}
                    totalMora={difMora(selected)}
                    totalGc={difGc(selected)}
                    canPay={puedePago && !selected.integrantes?.length}
                    onPagado={reloadDetail}
                  />
                </div>
              )}

              {/* Contrato firmado + documentos del crédito */}
              <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="contrato-credito">
                <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={subirContrato} />
                {(() => {
                  const contrato = docsCred.find((d) => d.tipo_documento === "contrato_firmado");
                  const bienDocs = docsCred.filter((d) => d.tipo_documento !== "contrato_firmado");
                  return (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
                          <ShieldCheck className="h-4 w-4 text-accent" /> Contrato firmado
                        </h4>
                        <div className="flex items-center gap-2">
                          {contrato ? <StatusBadge value="Cargado" /> : <StatusBadge value="Faltante" />}
                          {contrato && (
                            <TooltipButton tooltip="Descargar el contrato firmado" size="sm" variant="outline" className="h-7 px-2" onClick={() => descargarDoc(contrato.id)} data-testid="contrato-descargar">
                              <Download className="h-3.5 w-3.5" />
                            </TooltipButton>
                          )}
                          <TooltipButton tooltip="Subir el PDF del contrato firmado por el cliente"
                            data-testid="contrato-subir-btn" size="sm" variant="outline" className="h-7"
                            disabled={upBusy || !puedePago} onClick={() => fileRef.current?.click()}>
                            {upBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                            {contrato ? "Reemplazar" : "Subir PDF"}
                          </TooltipButton>
                        </div>
                      </div>
                      {contrato && (
                        <p className="mt-1 text-[11px] text-slate-400">
                          {contrato.nombre_archivo} · cargado {fdate(contrato.fecha_carga)} · cifrado SHA-256 {String(contrato.hash_sha256 || "").slice(0, 8)}
                        </p>
                      )}
                      <p className="mb-2 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Documentos del crédito ({bienDocs.length})</p>
                      <div className="space-y-1.5">
                        {bienDocs.length === 0 && <p className="text-xs text-slate-400">Sin documentos del bien ligados a este crédito.</p>}
                        {bienDocs.map((d) => (
                          <div key={d.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-xs" data-testid={`credito-doc-${d.tipo_documento}`}>
                            <span className="flex items-center gap-2 text-slate-600"><FileCheck2 className="h-3.5 w-3.5 text-emerald-600" /> {docLabel(d.tipo_documento)}</span>
                            <TooltipButton tooltip="Descargar este documento del crédito"
                              size="sm" variant="outline" className="h-6 px-2" onClick={() => descargarDoc(d.id)}>
                              <Download className="h-3 w-3" />
                            </TooltipButton>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              {selected.integrantes?.length > 0 && (
                <ExigibilidadGrupoBloque credito={selected} />
              )}

              {selected.integrantes?.length > 0 && (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="cartera-integrantes">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-emerald-700">
                      <Users className="h-4 w-4" /> Integrantes del grupo · obligados solidarios del total
                    </h4>
                    <HojasIntegrantesButton credito={selected} />
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-2 py-2">Integrante</th><th className="px-2 py-2">Rol</th>
                          <th className="px-2 py-2 text-right">Monto</th><th className="px-2 py-2 text-right">Saldo</th>
                          <th className="px-2 py-2 text-right" title="Porción de la comisión de apertura atribuida a este integrante">Comisión</th>
                          <th className="px-2 py-2 text-right" title="IVA de la porción de comisión de este integrante">IVA comisión</th>
                          <th className="px-2 py-2 text-right" title="Monto neto que le corresponde recibir (monto − comisión − IVA)">Neto a ministrar</th>
                          <th className="px-2 py-2 text-right">Días atraso</th><th className="px-2 py-2 text-right">CAT</th>
                          <th className="px-2 py-2">Estatus</th><th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.integrantes.map((it) => (
                          <tr key={it.cliente_id} className="border-t border-slate-100" data-testid={`integrante-cartera-${it.cliente_id}`}>
                            <td className="px-2 py-1.5">
                              <p className="font-medium text-slate-700">{it.cliente}</p>
                              <p className="text-[10px] text-slate-400">{it.rfc}</p>
                            </td>
                            <td className="px-2 py-1.5">{it.rol === "titular" ? "Titular" : "Obligado"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{mxn(it.monto)}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{mxn(it.saldo_insoluto)}</td>
                            <td className="px-2 py-1.5 text-right font-mono" data-testid={`integrante-com-${it.cliente_id}`}>
                              {it.comision_integrante != null ? mxn(it.comision_integrante) : "—"}
                              {it.rol === "titular" && Number(it.ajuste_centavos_comision || 0) !== 0 && (
                                <span className="ml-1 text-[9px] text-amber-600" title="Ajuste de centavos residuales al titular">±¢</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono" data-testid={`integrante-ivacom-${it.cliente_id}`}>
                              {it.iva_comision_integrante != null ? mxn(it.iva_comision_integrante) : "—"}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono" data-testid={`integrante-neto-${it.cliente_id}`}>
                              {it.monto_ministrado_integrante != null ? mxn(it.monto_ministrado_integrante) : "—"}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">{it.dias_atraso || 0}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{it.cat != null ? `${it.cat}%` : "—"}</td>
                            <td className="px-2 py-1.5"><StatusBadge value={it.estatus || "Vigente"} /></td>
                            <td className="px-2 py-1.5 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <HojaIntegranteButton credito={selected} integrante={it} />
                                <AcuseDesembolsoIntegranteButton credito={selected} integrante={it} variant="compact" />
                                <TooltipButton tooltip="Registrar un pago dirigido solo a este integrante del grupo"
                                  size="sm" variant="outline" className="h-7 px-2" disabled={!puedePago}
                                  data-testid={`pagar-integrante-${it.cliente_id}`} onClick={() => openPay(it)}>Pagar</TooltipButton>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-400">Cada pago a un integrante se aplica a SU tabla. "Registrar pago" (abajo) sin seleccionar integrante reparte sobre el grupo (proporcional al saldo).</p>
                </div>
              )}

              {selected.tabla_amortizacion?.length > 0 && (selected.estado_tabla === "diaria" || selected.estado_tabla === "proyeccion") ? (
                // Crédito diario o proyección pre-desembolso: SIEMPRE lista simple
                <div className="mt-3 min-w-0" data-testid="tabla-amortizacion">
                  <h4 className="mb-1.5 font-heading text-sm font-semibold text-slate-700">
                    {selected.estado_tabla === "diaria" ? "Plan de pagos" : "Plan de pagos previsto"}
                  </h4>
                  <TablaAmortizacionView credito={selected} />
                </div>
              ) : selected.tabla_amortizacion?.length > 0 && (
                // `min-w-0`: el diálogo es un grid y esta tabla tenía min-content de 810 px, así
                // que estiraba la columna y recortaba 44 px de TODO el detalle por la derecha
                // (la X de cerrar, la 4ª tarjeta y el botón de cancelar).
                <div className="mt-3 min-w-0" data-testid="tabla-amortizacion">
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="font-heading text-sm font-semibold text-slate-700">
                      {esCreditoArrendamiento(selected) ? TITULO_RENTAS : "Tabla de amortización"}{selected.integrantes?.length ? " (consolidada del grupo)" : ""}
                      {selected.estado_tabla === "definitiva" && (
                        <span className="ml-2 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700" data-testid="badge-definitiva">
                          <ShieldCheck className="mr-0.5 h-3 w-3" /> Definitiva
                        </span>
                      )}
                      {selected.estado_tabla === "proyeccion" && (
                        <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-800" data-testid="badge-proyeccion">
                          Proyección
                        </span>
                      )}
                    </h4>
                    {selected.estado_tabla === "definitiva" && (
                      <Button data-testid="descargar-tabla-pdf-btn" size="sm" variant="outline"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/creditos/${selected.id}/tabla-pdf`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url; a.download = `tabla-amortizacion-${selected.numero_credito || selected.id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (e) { toast.error("No se pudo descargar el PDF"); }
                        }}>
                        <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                      </Button>
                    )}
                  </div>
                  {esCreditoFactoraje(selected)
                    ? <TablaFactoraje tabla={selected.tabla_amortizacion} dataTestid="tabla-factoraje-credito" />
                    : esCreditoArrendamiento(selected)
                    ? <TablaRentasCredito tabla={selected.tabla_amortizacion} testId="tabla-rentas-credito" />
                    : <TablaAmortizacionCredito tabla={selected.tabla_amortizacion}
                        resaltarTotal={selected.tipo_amortizacion === "francesa_total_fijo"} />}
                </div>
              )}

              <div className="mt-2 flex items-center justify-between">
                <h4 className="font-heading text-sm font-semibold text-slate-700">Pagos del crédito</h4>
                <Button data-testid="open-pago-btn" size="sm" onClick={() => openPay()} disabled={!puedePago} className="bg-accent hover:bg-forest-700">
                  <Plus className="mr-1.5 h-4 w-4" /> Registrar pago
                </Button>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-3 py-2">Recibo</th><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Medio</th><th className="px-3 py-2">Aplicación (cap/int/iva/mora)</th><th className="px-3 py-2">Cuotas</th><th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2 text-right">Estatus / acción</th></tr>
                  </thead>
                  <tbody>
                    {pagos.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Sin pagos registrados</td></tr>}
                    {pagos.map((p) => {
                      const cancelado = p.estatus === "Cancelado";
                      return (
                      <tr key={p.id} className={`border-t border-slate-100 ${cancelado ? "opacity-50" : ""}`} data-testid={`pago-row-${p.id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{p.recibo}</td>
                        <td className="px-3 py-2 text-slate-500">{fdate(p.fecha_pago)}</td>
                        <td className="px-3 py-2">{p.medio_pago}{p.pago_tercero ? " · Tercero" : ""}{p.pago_efectivo ? " · Efectivo" : ""}{p.pagador_tipo === "deudor_factoraje" ? " · Deudor" : ""}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                          {mxn(p.aplicacion_capital)} / {mxn(p.aplicacion_interes)} / {mxn(p.aplicacion_iva)} / {mxn(p.aplicacion_moratorio)}
                          {p.remanente_generado > 0
                            ? <span className="ml-1 text-amber-700" data-testid={`pago-remanente-${p.id}`}>+ Remanente a favor del cliente {mxn(p.remanente_generado)}</span>
                            : p.saldo_a_favor > 0 ? <span className="ml-1 text-emerald-600">+SF {mxn(p.saldo_a_favor)}</span> : null}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{(p.cuotas_afectadas || []).join(", ") || "—"}</td>
                        <td className={`px-3 py-2 text-right font-mono ${cancelado ? "line-through" : ""}`}>{mxn(p.monto)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="inline-flex items-center justify-end gap-1">
                            <ReciboPagoButton pago={p} size="icon" />
                            <CancelarPagoButton pago={p} onDone={reloadDetail} />
                          </span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Registrar pago */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Registrar pago</DialogTitle>
            <DialogDescription className="sr-only">Captura un pago que se aplicará contra la tabla de amortización del crédito</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {selected?.integrantes?.length > 0 && (
              <div className="col-span-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="pago-destino">
                {pago.integrante_cliente_id
                  ? <>Pago dirigido al integrante: <b>{pago.integrante_nombre}</b> — se aplica a su tabla del grupo.</>
                  : <>Pago del <b>grupo completo</b>: se reparte proporcional al saldo de cada integrante.</>}
              </div>
            )}
            {selected?.tabla_amortizacion?.length > 0 && !selected?.integrantes?.length && !esCreditoArrendamiento(selected) && (
              <div className="col-span-2">
                <Label className="mb-1 block text-xs">Tipo de pago</Label>
                <Select value={pago.tipo_pago || "normal"} onValueChange={(v) => setP("tipo_pago", v)}>
                  <SelectTrigger data-testid="pago-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Pago normal — aplica según la tabla</SelectItem>
                    <SelectItem value="abono_capital">Abono a capital — pago anticipado, recalcula el crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="mb-1 block text-xs">Fecha de pago</Label><Input type="date" data-testid="pago-fecha" value={pago.fecha_pago} onChange={(e) => setP("fecha_pago", e.target.value)} disabled={user?.role !== "admin_entidad"} />{user?.role !== "admin_entidad" && <p className="mt-1 text-[11px] text-slate-400">El pago se registra con la fecha de hoy. Solo el administrador puede capturar otra fecha.</p>}</div>
            <div><Label className="mb-1 block text-xs">Monto</Label><MoneyInput data-testid="pago-monto" value={pago.monto} onChange={(v) => setP("monto", v)} /></div>
            {pago.tipo_pago !== "abono_capital" && revPago?.aviso && (
              <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900" data-testid="pago-aviso-reversion-cargo">
                {revPago.aviso}
              </div>
            )}
            <div><Label className="mb-1 block text-xs">Medio de pago</Label>
              <Select value={pago.medio_pago} onValueChange={(v) => {
                // H4 (feb-2026) — Sincronización bidireccional select ↔ toggle "Pago en efectivo".
                // Si el operador elige Efectivo, el toggle debe encenderse; si elige cualquier otro,
                // el toggle debe apagarse. Con esto el motor PLD nunca queda incoherente vs UI.
                setPago((prev) => ({ ...prev, medio_pago: v, pago_efectivo: v === "Efectivo" }));
              }}>
                <SelectTrigger data-testid="pago-medio"><SelectValue /></SelectTrigger>
                <SelectContent>{["Transferencia SPEI", "Efectivo", "Domiciliación", "Cheque", "Tarjeta"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1 block text-xs">Banco</Label><Input value={pago.banco} onChange={(e) => setP("banco", e.target.value)} /></div>
            <div><Label className="mb-1 block text-xs">Referencia</Label><Input value={pago.referencia} onChange={(e) => setP("referencia", e.target.value)} /></div>
            {pagoPorOt && (
              <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900" data-testid="pago-aviso-opcion-terminal">
                {MSG_PAGO_RENTAS_CUBIERTAS}
              </div>
            )}
            {pago.tipo_pago !== "abono_capital" && !pagoPorOt && proximaCuota(selected) && (() => {
              // Sugerido UNIFICADO (jul-2026): util compartida (valores del backend, base 360).
              const _pc = proximaCuota(selected);
              const _sug = sugeridoDePago(credPago, _pc);
              const _esParcial = _sug.esParcial;
              return (
              <div className="col-span-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="pago-cuota-objetivo">
                Se aplicará contra la cuota #{_pc.no} (vence {fdate(_pc.fecha_pago)}, cuota {mxn(_pc.pago)}{_esParcial ? ` — pendiente ${mxn(_sug.cuota)}` : ""}).
                {!pago.integrante_cliente_id && (_sug.difMora > 0 || _sug.difGc > 0 || _esParcial || _sug.moraCorriente > 0 || _sug.cuotaComision > 0) && (
                  <div className="mt-1.5 space-y-0.5 font-mono text-[11px] tabular-nums" data-testid="pago-diferidos-desglose">
                    <p className="font-sans font-semibold">Sugerido para saldar el periodo:</p>
                    {_sug.cuotaComision > 0 && (
                      <div className="flex justify-between" data-testid="pago-comision-cuota">
                        <span>+ Comisión de apertura de la cuota (+ IVA)</span>
                        <span>{mxn(_sug.cuotaComision)}</span>
                      </div>
                    )}
                    {_sug.moraCorriente > 0 && (
                      <div className="flex justify-between" data-testid="pago-mora-corriente">
                        <span>Intereses moratorios{_sug.moraDiasCorriente ? ` (${_sug.moraDiasCorriente} días de atraso)` : ""} — cuota #{_pc.no}</span>
                        <span>{mxn(_sug.moraCorriente)}</span>
                      </div>
                    )}
                    {_sug.ivaMoraCorriente > 0 && (
                      <div className="flex justify-between" data-testid="pago-iva-mora-corriente">
                        <span>+ IVA sobre intereses moratorios</span>
                        <span>{mxn(_sug.ivaMoraCorriente)}</span>
                      </div>
                    )}
                    {_sug.difDetalle.map((m) => (
                      <div key={`dif-${m.cuota_no}`} data-testid={`pago-mora-diferida-${m.cuota_no}`}>
                        <div className="flex justify-between">
                          <span>Moratorios periodo anterior{m.dias_atraso ? ` (${m.dias_atraso} días de atraso)` : ""} — cuota #{m.cuota_no}</span>
                          <span>{mxn(m.pendiente)}</span>
                        </div>
                        {m.base_capital > 0 && m.tasa > 0 && m.dias_atraso > 0 && (
                          <p className="text-[10px] text-sky-600" data-testid={`pago-mora-calculo-${m.cuota_no}`}>
                            {mxn(m.base_capital)} × {m.tasa}% anual × {m.dias_atraso}/360 días
                          </p>
                        )}
                      </div>
                    ))}
                    {_sug.difMoraIva > 0 && (
                      <div className="flex justify-between" data-testid="pago-iva-mora-diferida">
                        <span>+ IVA sobre moratorios diferidos</span><span>{mxn(_sug.difMoraIva)}</span>
                      </div>
                    )}
                    {_sug.difGc > 0 && (
                      <div className="flex justify-between" data-testid="pago-gc-diferido">
                        <span>+ {_sug.gcEtiqueta} diferidos</span><span>{mxn(_sug.difGc)}</span>
                      </div>
                    )}
                    {_sug.gcActivos > 0 && (_sug.gcDesglose ? (
                      <>
                        <div className="flex justify-between" data-testid="pago-gc-activo">
                          <span>+ {_sug.gcEtiqueta}{_sug.moraDiasCorriente ? ` (${_sug.moraDiasCorriente} días)` : ""}</span><span>{mxn(_sug.gcDesglose.monto)}</span>
                        </div>
                        {_sug.gcDesglose.iva > 0 && (
                          <div className="flex justify-between" data-testid="pago-gc-iva">
                            <span>+ {ivaEtiquetaGc(_sug.gcEtiqueta)}</span><span>{mxn(_sug.gcDesglose.iva)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex justify-between" data-testid="pago-gc-activo">
                        <span>+ {_sug.gcEtiqueta} (cargos activos + IVA)</span><span>{mxn(_sug.gcActivos)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between" data-testid="pago-cuota-periodo">
                      <span>+ Cuota del periodo{_esParcial ? " (pendiente)" : ""}</span><span>{mxn(_sug.cuota)}</span>
                    </div>
                    <div className="flex justify-between border-t border-sky-200 pt-0.5 font-semibold" data-testid="pago-total-sugerido">
                      <span>Total sugerido</span><span>{mxn(_sug.total)}</span>
                    </div>
                  </div>
                )}
                <span className="mt-1 block">El desglose lo calcula el sistema y aparece en el recibo. Si la cuota está en atraso, el pago salda primero la cuota normal; el moratorio se congela y se difiere al siguiente pago. El excedente pasa a las siguientes cuotas. La mora del siguiente periodo corre sobre el capital PENDIENTE real de cada cuota (sellado − pagado), no sobre el previsto en tabla — el capital nunca se incrementa por mora.</span>
              </div>
              );
            })()}
            <div className="col-span-2 flex items-center gap-6 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <label className="flex items-center gap-2 text-sm text-slate-700"><Switch data-testid="pago-efectivo" checked={pago.pago_efectivo} onCheckedChange={(v) => {
                // H4 (feb-2026) — Sincronización bidireccional toggle → select.
                // Si enciendes el toggle, medio_pago debe cambiar a "Efectivo".
                // Si lo apagas mientras el select decía Efectivo, cambia a "Transferencia SPEI" (default seguro).
                setPago((prev) => ({
                  ...prev, pago_efectivo: v,
                  medio_pago: v ? "Efectivo" : (prev.medio_pago === "Efectivo" ? "Transferencia SPEI" : prev.medio_pago),
                }));
              }} /> Pago en efectivo</label>
              {!esCreditoFactoraje(selected) && (
                <label className="flex items-center gap-2 text-sm text-slate-700"><Switch data-testid="pago-tercero" checked={pago.pago_tercero} onCheckedChange={(v) => setP("pago_tercero", v)} /> Pago de tercero</label>
              )}
            </div>
            {esCreditoFactoraje(selected) && (
              <PagadorFactorajeSelect className="col-span-2" credito={selected} value={pago.pagador}
                nombreTercero={pago.nombre_tercero} onChange={(patch) => setPago((p) => ({ ...p, ...patch }))} />
            )}
            {pago.pago_tercero && !esCreditoFactoraje(selected) && (
              <div className="col-span-2"><Label className="mb-1 block text-xs">Nombre del tercero</Label><Input value={pago.nombre_tercero} onChange={(e) => setP("nombre_tercero", e.target.value)} /></div>
            )}
            <p className="col-span-2 text-xs text-slate-400">Pagos en efectivo relevantes o de terceros pueden generar una alerta PLD automática.</p>
            {pago.tipo_pago === "abono_capital" && (
              <div className="col-span-2">
                <PrepagoCapital credito={selected} pagoBase={pago}
                  esMigrado={pagos.some((p) => p.pago_historico_migracion)}
                  onSetMonto={(v) => setP("monto", v)}
                  onDone={async () => { setPayOpen(false); await reloadDetail(); }} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancelar</Button>
            {pago.tipo_pago !== "abono_capital" && (
              <Button data-testid="save-pago-btn" onClick={savePago} disabled={saving || pagoPorOt} className="bg-primary hover:bg-sidebar-hover">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Registrar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
